import { Checkbox, NumberInput, Select, Slider, Stack, Text } from "@mantine/core";

import {
  FILTER_PARAMS,
  filterValues,
  heldValue,
  isFilter,
  paramLabel,
  type EffectValue,
  type FilterParam,
  type Point,
} from "./effect-filters";
import { ColorField, ParamSection } from "./params";
import type { EffectClip, TimelineEdit } from "./timeline";
import classes from "./editor.module.css";

/**
 * Live's Configuration of an effect (#265): a Pixi filter's controls, in live's order, with its
 * defaults, ranges and steps (`effect-filters.ts`); every other effect, and Grayscale Filter,
 * has none to change, in live's words.
 */
export function EffectSettings({ effect, onEdit }: { effect: EffectClip; onEdit: (edit: TimelineEdit) => void }) {
  const params = isFilter(effect.effect) ? FILTER_PARAMS[effect.effect] : [];
  if (!isFilter(effect.effect) || params.length === 0) {
    return (
      <Text size="xs" c="dimmed" fs="italic" ta="center" py="md">
        Properties not available for modification
      </Text>
    );
  }
  const values = filterValues(effect.effect, effect.values);
  const set = (name: string, value: EffectValue) =>
    onEdit({ type: "setClip", id: effect.id, patch: { effectValues: { [name]: value } } });
  return (
    <ParamSection title="Configuration">
      {params.map((param) => (
        <Control key={param.name} param={param} value={values[param.name]} onChange={(value) => set(param.name, value)} />
      ))}
    </ParamSection>
  );
}

function Control(props: { param: FilterParam; value: EffectValue | undefined; onChange: (value: EffectValue) => void }) {
  const { param, onChange } = props;
  const label = paramLabel(param.name);
  const value = heldValue(param, props.value);
  switch (param.kind) {
    case "range":
      return (
        <Level label={label} min={param.min} max={param.max} step={param.step} value={value as number} onChange={onChange} />
      );
    case "point": {
      const at = value as Point;
      return (
        <Stack gap={4}>
          <Text className={classes.paramLabel}>{label}</Text>
          {(["x", "y"] as const).map((axis) => (
            <Level
              key={axis}
              label={axis.toUpperCase()}
              spoken={`${label} ${axis.toUpperCase()}`}
              min={param.min}
              max={param.max}
              step={param.step}
              value={at[axis]}
              onChange={(next) => onChange({ ...at, [axis]: next })}
            />
          ))}
        </Stack>
      );
    }
    case "color":
      return (
        <div className={classes.paramRow}>
          <Text className={classes.paramLabel}>{label}</Text>
          <ColorField label={label} value={value as string} onCommit={onChange} />
        </div>
      );
    case "flag":
      return (
        <div className={classes.paramRow}>
          <Text className={classes.paramLabel}>{label}</Text>
          <Checkbox aria-label={label} checked={value as boolean} onChange={(event) => onChange(event.currentTarget.checked)} />
        </div>
      );
    case "choice":
      return (
        <div className={classes.paramRow}>
          <Text className={classes.paramLabel}>{label}</Text>
          <Select
            aria-label={label}
            placeholder="Select type"
            w={160}
            size="xs"
            allowDeselect={false}
            data={param.options.map((option) => ({ value: String(option.value), label: option.label }))}
            value={String(value)}
            onChange={(next) => {
              if (next !== null) onChange(Number(next));
            }}
          />
        </div>
      );
  }
}

/** Live's slider with its number box beside it, 56 px wide, both kept to the range. */
function Level(props: {
  label: string;
  spoken?: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
}) {
  const { label, min, max, step, value, onChange } = props;
  return (
    <div className={classes.levelRow}>
      <Text className={classes.paramLabel}>{label}</Text>
      <Slider
        className={classes.levelSlider}
        min={min}
        max={max}
        step={step}
        value={value}
        label={null}
        thumbLabel={props.spoken ?? label}
        onChange={onChange}
      />
      <NumberInput
        aria-label={props.spoken ?? label}
        w={56}
        size="xs"
        hideControls
        min={min}
        max={max}
        step={step}
        clampBehavior="strict"
        value={value}
        onChange={(next) => {
          if (typeof next === "number" && Number.isFinite(next)) onChange(next);
        }}
      />
    </div>
  );
}
